import {
    Component,
    OnInit,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    EventEmitter,
    ViewChild,
    OnDestroy,
    Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FTPService } from 'app/shared/services/ftp-service';
import { UiUtilsService } from 'app/shared/utils/ui-utils.service';
import { FtpSweepModel } from 'app/shared/models/ftp-sweep';
import { AdsPrismFtpAddEditComponent } from '../ads-prism-ftp-add-edit/ads-prism-ftp-add-edit.component';
import { customerEditorQueryParam } from 'app/shared/models/customer';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatSort } from '@angular/material/sort';
import { DomOperationUtilsService } from 'app/shared/utils/dom-operation-utils.service';
import { Subscription } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';
import { CustomerEditorPaginationState } from '../customer-editor-footer.model';
import { TranslateService } from '@ngx-translate/core';
import { IComponentModalResult } from 'app/shared/models/modal';

@Component({
    selector: 'app-ads-prism-ftp-sweep',
    templateUrl: './ads-prism-ftp-sweep.component.html',
    styleUrls: ['./ads-prism-ftp-sweep.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdsPrismFtpSweepComponent implements OnInit, OnDestroy {
    @Output() public paginationStateChange = new EventEmitter<CustomerEditorPaginationState>();

    @ViewChild(MatPaginator) public paginator: MatPaginator;
    @ViewChild(MatSort) public sort: MatSort;
    public ftpDataSource: MatTableDataSource<FtpSweepModel>;
    public ftpDescriptionColumns = ['protocol', 'server', 'action'];

    public protocols = ['SFTP', 'FTP', 'FTPS'];

    public customerId = 0;

    public loadingState = false;
    public totalPaginationLength = 0;

    public showpageHint = false;
    private subscriptions: Subscription[] = [];
    private addDialogRef: MatDialogRef<AdsPrismFtpAddEditComponent>;
    constructor(
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private snackBar: MatSnackBar,
        private uiUtilsService: UiUtilsService,
        private activeRoute: ActivatedRoute,
        private ftpService: FTPService,
        private domOperationUtilsService: DomOperationUtilsService,
        private translate: TranslateService,
    ) { }

    ngOnInit() {
        this.subscriptions.push(this.domOperationUtilsService.ftpDialogOpened.subscribe((shouldOpen: boolean) => {
            if (shouldOpen && !this.addDialogRef) {
                this.addNewFTPServer();
            } else if (!shouldOpen && this.addDialogRef) {
                this.addDialogRef.close({ success: false });
                this.addDialogRef = null;
            }

            this.uiUtilsService.safeChangeDetection(this.cdr);
        }));
        // get current query params for customer
        this.customerId = Number(this.activeRoute.snapshot.queryParamMap.get(customerEditorQueryParam));
        this.refreshTable();
    }

    public addNewFTPServer() {
        this.addDialogRef = this.dialog
            .open(AdsPrismFtpAddEditComponent, {
                disableClose: true,
                hasBackdrop: false,
                panelClass: 'ads-prism-ftp-add-edit',
            });

        this.domOperationUtilsService.ftpDialogOpened.next(true);

        this.addDialogRef.afterClosed().subscribe((result: IComponentModalResult | undefined) => {
            this.addDialogRef = null;
            this.domOperationUtilsService.ftpDialogOpened.next(false);
            if (result?.success) {
                this.showChangesSavedToast();
                this.refreshTable();
            }
        });
    }

    public deleteFTPServer(guid: string) {
        this.ftpService.deleteFtpServer(this.customerId, guid).subscribe((res) => {
            if (res && res.isError) {
                this.snackBar.open('Error!', 'Technical error', {
                    panelClass: 'custom-error-snack-bar',
                });
            } else {
                this.showChangesSavedToast();
                this.ftpDataSource.data = this.ftpDataSource.data.filter((o) => o.guid !== guid);
                this.totalPaginationLength = this.ftpDataSource.data.length;
                this.emitPaginationState();
                this.uiUtilsService.safeChangeDetection(this.cdr);
            }
        });
    }

    public editFTPServer(server: FtpSweepModel) {
        this.addDialogRef = this.dialog
            .open(AdsPrismFtpAddEditComponent, {
                disableClose: true,
                data: server,
                hasBackdrop: false
            });

        this.domOperationUtilsService.ftpDialogOpened.next(true);
        this.addDialogRef.afterClosed().subscribe((result: IComponentModalResult | undefined) => {
            this.addDialogRef = null;
            this.domOperationUtilsService.ftpDialogOpened.next(false);
            if (result?.success) {
                this.showChangesSavedToast();
                this.refreshTable();
            }
        });
    }

    private refreshTable() {
        this.loadingState = true;
        this.uiUtilsService.safeChangeDetection(this.cdr);
        this.ftpService.getFtpServers(this.customerId).subscribe(
            (data: Array<FtpSweepModel>) => {
                if (data) {
                    this.ftpDataSource = new MatTableDataSource(data);
                } else {
                    this.ftpDataSource = new MatTableDataSource([]);
                }
                this.totalPaginationLength = this.ftpDataSource.data.length;
                this.ftpDataSource.paginator = this.paginator;
                this.ftpDataSource.sort = this.sort;
                this.loadingState = false;
                this.emitPaginationState();
                this.uiUtilsService.safeChangeDetection(this.cdr);
            },
            () => {
                this.ftpDataSource = new MatTableDataSource([]);
                this.totalPaginationLength = 0;
                this.loadingState = false;
                this.emitPaginationState();
                this.uiUtilsService.safeChangeDetection(this.cdr);
            },
        );
    }

    private showChangesSavedToast(): void {
        this.snackBar.open(
            this.translate.instant('CUSTOMER_EDITOR.CHANGES_SAVED_TO_CUSTOMER'),
            this.translate.instant('COMMON.DISMISS_TEXT'),
            { duration: 10000 },
        );
    }

    public applyPagination(page: PageEvent): void {
        this.paginator.pageIndex = page.pageIndex;
        this.paginator.pageSize = page.pageSize;
        this.paginator.page.emit(page);
        this.emitPaginationState();
    }

    private emitPaginationState(): void {
        this.paginationStateChange.emit({
            length: this.totalPaginationLength,
            pageIndex: this.paginator?.pageIndex ?? 0,
            pageSize: this.paginator?.pageSize ?? 10,
            pageSizeOptions: [10, 25, 100],
        });
    }

    ngOnDestroy(): void {
        for(const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
        this.subscriptions = [];
    }
}
